import type { ChangeEvent, ReactNode, SyntheticEvent } from "react"
import { useEffect, useRef, useState } from "react"
import { useField, useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { AutocompleteRenderGroupParams, ListSubheader } from "@mui/material"

import { ApiQueryAutocomplete } from "../../../../../../core/api/components/ApiQueryAutocomplete"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ConfigQuery, fetchTimelineResources, type TimelineResourcesResponse } from "../../../createOrderApi"
import { getFormikFieldError } from "../../form/helpers"
import { DELIVERY_LOCATION_HAS_OPTIONS_FIELD } from "../../form/formLib"
import { DynamicConfigField, MeetingServiceConfigMode, MeetingServiceTabValues } from "../../form/formTypes"
import { ResourceType } from "../../../createOrderTypes"

const useStyles = makeStyles()((theme) => ({
    listbox: {
        padding: 0,
    },
    groupLabel: {
        fontSize: theme.typography.body1.fontSize,
        lineHeight: theme.typography.body1.lineHeight,
        fontWeight: 600,
        padding: "1rem",
        paddingBottom: ".25rem",
        color: theme.palette.tailwind.slate[950],
    },
    groupChildren: {
        padding: 0,
        margin: 0,
    },
}))

type DeliveryLocationAutocompleteGroupProps = Readonly<{
    group: AutocompleteRenderGroupParams["group"]
    children: ReactNode
}>

function DeliveryLocationAutocompleteGroup({ group, children }: DeliveryLocationAutocompleteGroupProps) {
    const { classes } = useStyles()

    return (
        <li>
            <ListSubheader component="div" className={classes.groupLabel}>
                {group}
            </ListSubheader>
            <ul className={classes.groupChildren}>{children}</ul>
        </li>
    )
}

type Resource = Readonly<{
    id: string
    name: string
    serviceCenterName: string
}>

const transformData = (data: TimelineResourcesResponse) => {
    const serviceCentersData = data?.serviceCenters

    if (!serviceCentersData) return []

    const allResources: Resource[] = serviceCentersData.flatMap((serviceCenter) =>
        serviceCenter.resources.map((resource) => ({
            id: String(resource.id),
            name: resource.displayName || resource.name,
            serviceCenterName: serviceCenter.name,
        })),
    )

    // MUI groups options based on the order they are provided, so keep them sorted by group label.
    return allResources.sort(
        (a, b) => a.serviceCenterName.localeCompare(b.serviceCenterName) || a.name.localeCompare(b.name),
    )
}

type DeliveryLocationAutocompleteProps = {
    userId: number
    config: DynamicConfigField
}

export function DeliveryLocationAutocomplete({ userId, config }: DeliveryLocationAutocompleteProps) {
    const { classes } = useStyles()
    const [search, setSearch] = useState("")
    const [open, setOpen] = useState(false)

    const autocompleteMode: MeetingServiceConfigMode = config.inputField?.mode || MeetingServiceConfigMode.LOAD_ON_OPEN

    // We must know if there are any options to decide whether to render the dropdown at all,
    // so we always allow a "probe" fetch on mount.
    const shouldProbeOptions = true

    const baseEnabled =
        (autocompleteMode === MeetingServiceConfigMode.SEARCH_AS_YOU_TYPE && search.length >= 2) ||
        (autocompleteMode === MeetingServiceConfigMode.LOAD_ON_OPEN && open)

    const [field, meta, helpers] = useField(config.key)
    const { setFieldValue, submitCount } = useFormikContext<MeetingServiceTabValues & Record<string, unknown>>()
    const formikErrorProps = getFormikFieldError(meta, submitCount)

    const [optionsSnapshot, setOptionsSnapshot] = useState(false)
    const shouldHydrateFromValue = Boolean(field.value) && !optionsSnapshot
    const enabled = baseEnabled || shouldHydrateFromValue || shouldProbeOptions

    const {
        data: options = [],
        isError,
        isLoading,
    } = useApiQuery(fetchTimelineResources, {
        queryName: ConfigQuery.TIMELINE_RESOURCES_FOR_USER,
        dependencies: {
            userId,
            resourceTypes: ResourceType.ROOM,
        },
        select: transformData,
        enabled,
    })

    useEffect(() => {
        if (options.length > 0 && !optionsSnapshot) {
            setOptionsSnapshot((prev) => !prev)
        }
    }, [options, optionsSnapshot])

    const resolvedOptionsOnceRef = useRef(false)

    useEffect(() => {
        if (!enabled || isLoading || isError || resolvedOptionsOnceRef.current) {
            return
        }

        resolvedOptionsOnceRef.current = true
        const hasAnyOptions = options.length > 0
        setFieldValue(DELIVERY_LOCATION_HAS_OPTIONS_FIELD, hasAnyOptions, false)
    }, [enabled, isError, isLoading, options.length, setFieldValue])

    const handleInputChange = (_event: ChangeEvent<{}>, value: string) => {
        setSearch(value)
    }

    const handleChange = (_event: SyntheticEvent, value: Resource | null, reason?: string) => {
        if (value == null || reason === "clear") {
            helpers.setValue("", false)
            return
        }
        helpers.setValue(value.id, false)
    }

    // If there is nothing to select, the dropdown should not be shown.
    if (resolvedOptionsOnceRef.current && options.length === 0) {
        return null
    }

    return (
        <ApiQueryAutocomplete
            isLoading={enabled && isLoading}
            isError={isError}
            options={options}
            groupBy={(option) => option.serviceCenterName}
            renderGroup={(params) => (
                <DeliveryLocationAutocompleteGroup key={params.key} group={params.group}>
                    {params.children}
                </DeliveryLocationAutocompleteGroup>
            )}
            getOptionLabel={(option) => option.name || ""}
            value={options.find((opt) => opt.id === field.value) || null}
            onChange={handleChange}
            onInputChange={handleInputChange}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            size="small"
            ListboxProps={{
                className: classes.listbox,
            }}
            slotProps={{
                textField: formikErrorProps,
            }}
            label={`${config?.label || config?.description}${config?.inputField?.isRequired ? " *" : ""}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
        />
    )
}
